import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import type { ScheduleEvent, ScheduleEventLocation, ScheduleEventType } from '@shared/types';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { EVENT_TYPE_CONFIG, LOCATION_CONFIG } from './EventEditorPopover';

interface EventEditorModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  existingEvent?: ScheduleEvent;
  onSave: (event: {
    eventType: ScheduleEventType;
    location?: ScheduleEventLocation;
    note?: string;
  }) => void;
  onDelete?: () => void;
  isGoalDate?: boolean;
  goalLabel?: string;
}

/**
 * Modal editor for schedule events (used on mobile)
 * Full-screen dialog for editing event type, location, and notes
 */
export const EventEditorModal: React.FC<EventEditorModalProps> = ({
  open,
  onClose,
  selectedDate,
  existingEvent,
  onSave,
  onDelete,
  isGoalDate,
  goalLabel,
}) => {
  const [eventType, setEventType] = useState<ScheduleEventType | null>(
    existingEvent?.eventType ?? null
  );
  const [location, setLocation] = useState<ScheduleEventLocation | undefined>(
    existingEvent?.location
  );
  const [note, setNote] = useState(existingEvent?.note ?? '');

  // Reset state when opening for a new date
  useEffect(() => {
    if (open) {
      setEventType(existingEvent?.eventType ?? null);
      setLocation(existingEvent?.location);
      setNote(existingEvent?.note ?? '');
    }
  }, [open, existingEvent]);

  const handleSave = () => {
    if (eventType) {
      onSave({
        eventType,
        location,
        note: note.trim() || undefined,
      });
    }
    onClose();
  };

  const handleClear = () => {
    setEventType(null);
    setLocation(undefined);
    setNote('');
    if (existingEvent && onDelete) {
      onDelete();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {selectedDate ? format(selectedDate, 'EEEE, MMM d') : 'Edit Event'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close dialog">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Goal Date Indicator */}
        {isGoalDate && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: alpha('#ffc107', 0.1),
              borderRadius: 1,
              border: `1px solid ${alpha('#ffc107', 0.3)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2">🏆 {goalLabel || 'Goal Race Day'}</Typography>
          </Box>
        )}

        {/* Event Type Selection */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Training Type
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 3 }}>
          {(Object.keys(EVENT_TYPE_CONFIG) as ScheduleEventType[]).map(type => {
            const config = EVENT_TYPE_CONFIG[type];
            const isSelected = eventType === type;
            return (
              <Button
                key={type}
                variant={isSelected ? 'contained' : 'outlined'}
                onClick={() => setEventType(type)}
                aria-label={config.label}
                sx={{
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  bgcolor: isSelected ? alpha(config.color, 0.9) : 'transparent',
                  borderColor: config.color,
                  color: isSelected ? 'white' : config.color,
                  '&:hover': {
                    bgcolor: isSelected ? alpha(config.color, 1) : alpha(config.color, 0.1),
                    borderColor: config.color,
                  },
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>{config.emoji}</span>
                <Typography variant="body2" sx={{ textTransform: 'none', fontWeight: 500 }}>
                  {config.label}
                </Typography>
              </Button>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Location Selection (only if not rest day) */}
        {eventType && eventType !== 'rest' && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Location (optional)
            </Typography>
            <ToggleButtonGroup
              value={location}
              exclusive
              onChange={(_, value) => setLocation(value || undefined)}
              aria-label="Select location"
              sx={{ mb: 3, width: '100%' }}
            >
              {(Object.keys(LOCATION_CONFIG) as ScheduleEventLocation[]).map(loc => {
                const config = LOCATION_CONFIG[loc];
                return (
                  <ToggleButton
                    key={loc}
                    value={loc}
                    aria-label={config.label}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>{config.emoji}</span>
                    <Typography variant="body1" sx={{ textTransform: 'none' }}>
                      {config.label}
                    </Typography>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </>
        )}

        {/* Note field */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Note for AI Scheduler (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="e.g., Group run, rainy weather expected, limited time..."
          value={note}
          onChange={e => setNote(e.target.value)}
          inputProps={{
            maxLength: 500,
            'aria-label': 'Note for scheduler',
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {existingEvent && (
          <Button
            variant="text"
            color="error"
            onClick={handleClear}
            aria-label="Delete event"
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} aria-label="Cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!eventType}
          aria-label="Save event"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventEditorModal;
